<template>
  <nav class="global-header__nav" aria-label="グローバルナビゲーション">
    <ul class="nav-list">
      <li
        v-for="(item, idx) in navItems"
        :key="item.label"
        class="nav-list__item"
        :class="{
          'is-open': openNavIndex === idx,
          'nav-list__item--scout': item.label === 'スカウト/おすすめ',
          'nav-list__item--first': idx === 0,
          'nav-list__item--last': idx === navItems.length - 1,
        }"
        :data-active="isActive(item)"
        @mouseenter="handleNavEnter(idx, item)"
        @mouseleave="handleNavLeave"
      >
        <template v-if="!item.children?.length">
          <button
            v-if="item.action === 'scout'"
            class="nav-list__link"
            type="button"
            @click="handleScoutLeafClick(item)"
          >
            {{ item.label }}
            <span v-if="item.badgeCount && item.badgeCount > 0" class="nav-list__badge">
              {{ item.badgeCount > 99 ? '99+' : item.badgeCount }}
            </span>
          </button>
          <SpaInternalLink
            v-else-if="item.href"
            class="nav-list__link"
            :href="item.href"
            @click="onNavClick($event, item)"
          >
            {{ item.label }}
            <span v-if="item.badgeCount && item.badgeCount > 0" class="nav-list__badge">
              {{ item.badgeCount > 99 ? '99+' : item.badgeCount }}
            </span>
          </SpaInternalLink>
        </template>
        <button
          v-else
          class="nav-list__link nav-list__link--has-children"
          type="button"
          aria-haspopup="true"
          :aria-expanded="openNavIndex === idx"
        >
          {{ item.label }}
          <span v-if="item.badgeCount && item.badgeCount > 0" class="nav-list__badge">
            {{ item.badgeCount > 99 ? '99+' : item.badgeCount }}
          </span>
        </button>

        <div
          v-if="item.children?.length"
          :ref="(el) => setSubmenuRef(idx, el)"
          class="nav-submenu"
          :class="{ 'is-open': openNavIndex === idx }"
          :style="{ '--submenu-height': `${submenuHeights[idx] || 0}px` }"
          role="menu"
        >
          <template
            v-for="child in item.children"
            :key="child.href + (child.labelHtml || child.label)"
          >
            <SpaInternalLink
              class="nav-submenu__item"
              role="menuitem"
              :href="child.href"
              @click="onNavClick($event, child)"
            >
              <span v-html="child.labelHtml || child.label" />
            </SpaInternalLink>
          </template>
        </div>
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue';
import { ref } from 'vue';

import SpaInternalLink from '@/components/shared/SpaInternalLink.vue';
import type { NavChild, NavItem } from './types.d';

const props = defineProps<{
  navItems: NavItem[];
  isActive: (item: NavItem) => boolean;
  platform?: string;
}>();

const openNavIndex = ref<number | null>(null);
const submenuRefs = ref<Record<number, HTMLElement | null>>({});
const submenuHeights = ref<Record<number, number>>({});

const handleNavEnter = (idx: number, item: NavItem) => {
  if (item.children?.length) {
    openNavIndex.value = idx;
  } else {
    openNavIndex.value = null;
  }
};

const handleNavLeave = () => {
  openNavIndex.value = null;
};

const isScoutItem = (item: NavItem | NavChild): boolean =>
  item.action === 'scout' || item.href === '/scouts';

const logScoutAgentModal = () => {
  console.log(
    '[GlobalHeader] Open platform modal: #platformCurrentCandidateAgent (not implemented)'
  );
};

/** SpaInternalLink / `<a>` 用: エージェント時のスカウトは遷移させずモーダル予定 */
const onNavClick = (event: MouseEvent, item: NavItem | NavChild) => {
  if (isScoutItem(item) && props.platform === 'agent') {
    event.preventDefault();
    logScoutAgentModal();
  }
};

/** `action === 'scout'` のボタン用（href があれば agent 以外はフル遷移） */
const handleScoutLeafClick = (item: NavItem) => {
  if (isScoutItem(item) && props.platform === 'agent') {
    logScoutAgentModal();
    return;
  }
  if (item.href) {
    window.location.href = item.href;
  }
};

const setSubmenuRef = (idx: number, el: Element | ComponentPublicInstance | null) => {
  if (!el) return;
  const htmlEl = el as HTMLElement;
  submenuRefs.value[idx] = htmlEl;
  submenuHeights.value[idx] = htmlEl.scrollHeight;
};
</script>

<style scoped lang="scss">
@import '@/assets/css/_variables.scss';
@import '@/assets/stylesheets/components/_variables.scss';

.global-header__nav {
  background: $primary-dark;
  width: 100%;
  display: flex;
  justify-content: center;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  overflow: visible;
}

.nav-list {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  list-style: none;
  margin: 0;
  padding: 0;
}

.nav-list__item {
  position: relative;
  height: 53px;
  background: $primary-dark;
  box-sizing: border-box;
  transition: background 0.2s ease;
  border-bottom: 3px solid transparent;
}

.nav-list__item:first-child {
  border-bottom-left-radius: 12px;
}

.nav-list__item:last-child {
  border-bottom-right-radius: 12px;
}

.nav-list__item--scout {
  min-width: 104px;
}

.nav-list__badge {
  position: absolute;
  top: 6px;
  right: 14px;
  min-width: 22px;
  padding: 2px 6px;
  border-radius: 999px;
  background: #dc3545;
  color: #ffffff;
  font-size: 10px;
  line-height: 1.2;
  text-align: center;
  box-shadow: 0 0 0 1px #ffffff;
}

@media (max-width: 1024px) {
  .nav-list__badge {
    right: 10px;
    top: 8px;
    padding: 2px 5px;
    font-size: 9px;
  }
}

.nav-list__item--first[data-active='true'] {
  border-bottom-left-radius: 0;
}

.nav-list__item--first:not([data-active='true']):hover {
  border-bottom-left-radius: 0;
}

.nav-list__item--last[data-active='true'] {
  border-bottom-right-radius: 0;
}

.nav-list__item--last:not([data-active='true']):hover {
  border-bottom-right-radius: 0;
}

.nav-list__link {
  color: #ffffff;
  font-weight: 700;
  font-size: 14px;
  text-decoration: none;
  letter-spacing: 0.55px;
  line-height: 21px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  border: none;
  background: transparent;
  font: inherit;
  position: relative;
}

[data-active='true'] {
  background: #f0f9fc;
  border-bottom: 3px solid $primary-dark;
}

[data-active='true'] .nav-list__link {
  color: $primary-dark;
  pointer-events: none;
}

.nav-list__item:not([data-active='true']):hover,
.nav-list__item.is-open:not([data-active='true']) {
  background: #f0f9fc;
  border-bottom: 3px solid $primary-dark;
}

.nav-list__item:not([data-active='true']):hover .nav-list__link,
.nav-list__item.is-open:not([data-active='true']) .nav-list__link {
  color: $primary-dark;
}

.nav-list__link--has-children {
  cursor: default;
}

.nav-submenu {
  position: absolute;
  top: 53px;
  left: 0;
  width: 100%;
  background: #ffffff;
  box-shadow: 0 10px 28px #1e1e1e17;
  border-radius: 0 0 12px 12px;
  overflow: hidden;
  z-index: $z-global-header-dropdown;
  max-height: 0;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.18s ease, max-height 0.18s ease;
}

.nav-submenu__item {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 14px 18px;
  color: #0090c2;
  font-size: 14px;
  line-height: 1.5;
  text-decoration: none;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.2s ease;
}

.nav-submenu__item:last-child {
  border-bottom: none;
}

.nav-submenu__item:hover,
.nav-submenu__item:focus-visible {
  background: #f7f8fb;
  outline: none;
}

.nav-submenu.is-open {
  max-height: var(--submenu-height, 0px);
  opacity: 1;
  pointer-events: auto;
}
</style>
